import { ApiClient } from '@center-technologies/shared-api-clients';
import type { CheckInScheduleTemplate } from 'types/spherePoints';

export interface ScheduleTemplatesQueryParams {
  locale?: string;
  includeInactive?: boolean;
}

export interface ScheduleTemplatesResponse {
  success: boolean;
  data: CheckInScheduleTemplate[];
  count: number;
}

/**
 * Fetches check-in schedule templates from the schema service.
 *
 * Templates are pre-configured schedule configurations that can be used
 * when creating manual status factors with scheduled check-ins.
 *
 * @param apiClient - An initialized ApiClient pointed at the schema service.
 * @param params - Query parameters: locale, includeInactive (the organization is the active one).
 * @returns An array of schedule templates with localized names and descriptions.
 * @throws Error object on failure.
 */
export async function getScheduleTemplates(
  apiClient: ApiClient,
  params: ScheduleTemplatesQueryParams = {}
): Promise<CheckInScheduleTemplate[]> {
  // Build query string
  const queryParams = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');

  const queryString = queryParams ? `?${queryParams}` : '';
  const path = `/schedule-templates${queryString}`;

  try {
    const response = await apiClient.get<ScheduleTemplatesResponse>(path);
    // ApiClient.get returns the response body directly (not wrapped in axios response)
    // response is ScheduleTemplatesResponse { success, data, count }
    return response.data || [];
  } catch (err: any) {
    const errorMsg = err.response?.data?.error ?? err.message ?? 'Failed to fetch schedule templates';
    console.error('[getScheduleTemplates] Error:', errorMsg);
    // Return empty array on error to allow graceful degradation
    return [];
  }
}
